import { IonActionSheet, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCheckbox, IonCol, IonContent, IonFooter, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonLoading, IonPage, IonRow, IonTitle, IonToolbar, useIonToast } from "@ionic/react";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import jwt_decode from "jwt-decode";
import { useTranslation } from "react-i18next";
import "./tsPayResponse.css";
import { useTenant } from "../../hooks/multiTenancy";
import { useSelectedSalesPoint } from "../../hooks/selectedSalesPoint";
import { getNonFidelityAppSalesPoint } from "../../utils";
import { useMutation } from "@apollo/client";
import { orderPayedMutation, orderPayedWithMailMutation } from "../../apollo-client/queries";
import { ExternalOrdersPaymentType, SourceType } from "../../apollo-client/types";
import { warning, card, logoPaypal } from "ionicons/icons";

type ModalProps = {
  guestModal?: boolean;
};

const TsPayResponse: React.FC<ModalProps> = ({ guestModal = false }) => {
  const [orderPayed, orderPayedResponse] = useMutation(orderPayedMutation);
  const [orderPayedWithMail, orderPayedWithMailResponse] = useMutation(orderPayedWithMailMutation);
  const { search: searchQuerystring } = useLocation();
  const [toast] = useIonToast();
  const { t } = useTranslation();
  const history = useHistory();
  const { idTable, idOrder } = useParams<{ idTable?: string; idOrder: string }>();
  const tenant = useTenant();
  const [selectedSalesPointId] = useSelectedSalesPoint();
  const selectedSalesPoint = getNonFidelityAppSalesPoint(tenant, selectedSalesPointId);
  const [eReceipt, setEReceipt] = useState<boolean>(false);
  const [eReceiptMail, setEReceiptMail] = useState<string>("");
  const eReceiptEnabled: boolean | undefined = selectedSalesPoint?.salesPoint.externalOrdersSettings?.eReceiptEnabled;
  const eCustomerEnabled: boolean | undefined = selectedSalesPoint?.salesPoint.externalOrdersSettings?.eCustomerEnabled;

  const { data: dataParam } = queryString.parse(searchQuerystring);
  const json = useMemo(
    () => 
      dataParam
      ? jwt_decode<{ state: "active" | "error" | "failed", declineCode: string }>(dataParam as string)
      : undefined
  , [dataParam]);

  const externalOrdersSettings = selectedSalesPoint?.salesPoint?.externalOrdersSettings;
  const eopTSPay = externalOrdersSettings?.acceptedPayments?.filter((eop) => eop.paymentType === ExternalOrdersPaymentType.TSPAY);
  const sourceTypes = eopTSPay && eopTSPay.length > 0 ? eopTSPay[0].sourceTypes : undefined;

  const [showActionSheet, setShowActionSheet] = useState(false);

  const confirm = useCallback(async () => {
    try {
      let url = guestModal ? `/payment-response/${idOrder}` : `/app/orders/${idOrder}`;
      if (eReceipt && eReceiptMail.length > 0) {
        if (guestModal) {
          url = `/payment-response-mail/${idOrder}`;
        }
        await orderPayedWithMail({
          variables: { idSalesPoint: selectedSalesPointId, idOrder, email: eReceiptMail, prepaymentType: "TSPAY" },
        });
      } else {
        await orderPayed({
          variables: { idSalesPoint: selectedSalesPointId, idOrder, prepaymentType: "TSPAY" },
        });
      }
      toast({
        cssClass: "order-payed-toast",
        color: "light",
        message: t("pay_order.payed"),
        duration: 1000,
        animated: true,
        position: "middle",
        onDidDismiss: () => {
          if (guestModal) {
            history.replace(url);
          } else {
            history.replace("/app/orders");
            history.push(url);
          }
        },
      });
    } catch (e) {
      toast({
        cssClass: "order-payed-toast",
        color: "light",
        message: t("pay_order.tsPayErrorMsg"),
        duration: 2000,
        animated: true,
        position: "middle",
        onDidDismiss: () => {
          history.replace(guestModal ? `/select/table/${selectedSalesPointId}/${idTable!}` : "/app/orders");
        },
      });
    }
  }, [guestModal, idOrder, selectedSalesPointId, eReceipt, eReceiptMail]);

  useEffect(() => {
    if (json && json.state === "active" && !eReceiptEnabled) {
      confirm();
    }
  }, [json, eReceiptEnabled]);

  const pay = () => {
    if (sourceTypes && sourceTypes.length > 1) {
      setShowActionSheet(true);
    } else if (sourceTypes && sourceTypes.length == 1) {
      history.replace(guestModal ? `/tspay/${idTable!}/${idOrder}/${sourceTypes[0]}` : `/app/orders/tspay/${idOrder}/${sourceTypes[0]}`);
    }
  };

  const getErrorString = (declineCode: string): string => {
    switch (declineCode) {
      case "insufficient_funds":
        return t("pay_order.tsPayErrorBody.insufficientFunds");
      case "card_velocity_exceeded":
      case "withdrawal_count_limit_exceeded":
        return t("pay_order.tsPayErrorBody.cardVelocityExceeded");
      case "invalid_account":
      case "new_account_information_available":
        return t("pay_order.tsPayErrorBody.invalidAccount");
      case "restricted_card":
      case "pickup_card":
        return t("pay_order.tsPayErrorBody.restrictedCard");
      case "authentication_required":
      case "setup_intent_authentication_failure":
        return t("pay_order.tsPayErrorBody.authenticationFailed");
      case "card_not_supported":
        return t("pay_order.tsPayErrorBody.cardNotSupported");
      case "currency_not_supported":
        return t("pay_order.tsPayErrorBody.currencyNotSupported");
      case "invalid_number":
      case "incorrect_number":
      case "testmode_decline":
        return t("pay_order.tsPayErrorBody.invalidNumber");
      case "invalid_cvc":
      case "incorrect_cvc":
        return t("pay_order.tsPayErrorBody.invalidCvc");
      case "invalid_pin":
      case "incorrect_pin":
        return t("pay_order.tsPayErrorBody.invalidPin");
      case "expired_card":
        return t("pay_order.tsPayErrorBody.expiredCard");
      case "incorrect_zip":
        return t("pay_order.tsPayErrorBody.incorrectZip");
      case "invalid_amount":
        return t("pay_order.tsPayErrorBody.invalidAmount");
      case "invalid_expiry_year":
        return t("pay_order.tsPayErrorBody.invalidExpiryYear");
      case "invalid_expiry_month":
        return t("pay_order.tsPayErrorBody.invalidExpiryMonth");
      case "duplicate_transaction":
        return t("pay_order.tsPayErrorBody.duplicateTransaction");
      case "issuer_not_available":
        return t("pay_order.tsPayErrorBody.issuerNotAvailable");
      case "not_permitted":
        return t("pay_order.tsPayErrorBody.notPermitted");
      case "try_again_later":
        return t("pay_order.tsPayErrorBody.tryAgain");
      case "pin_try_exceeded":
        return t("pay_order.tsPayErrorBody.pinTryExceeded");
      case "offline_pin_required":
      case "online_or_offline_pin_required":
        return t("pay_order.tsPayErrorBody.pinRequired");
      case "reenter_transaction":
        return t("pay_order.tsPayErrorBody.reenterTransaction");
      case "approve_with_id":
        return t("pay_order.tsPayErrorBody.approveWithId");
      case "processing_error":
        return t("pay_order.tsPayErrorBody.processingError");
      case "call_issuer":
      case "do_not_honor":
      case "do_not_try_again":
      case "fraudulent":
      case "lost_card":
      case "merchant_blacklist":
      case "no_action_taken":
      case "revocation_of_all_authorizations":
      case "revocation_of_authorization":
      case "security_violation":
      case "service_not_allowed":
      case "stolen_card":
      case "stop_payment_order":
      case "transaction_not_allowed":
      case "generic_decline":
      default:
        return t("pay_order.tsPayErrorBody.genericDecline");
    }
  };

  return (
    <IonPage className="pay-order">
      <IonHeader>
        <IonToolbar color="secondary">
          <IonButtons slot="start"></IonButtons>
          <IonTitle>{t("pay_order.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonLoading isOpen={orderPayedResponse.loading || orderPayedWithMailResponse.loading} />
      <IonContent fullscreen>
        {json && json.state === "active" && eReceiptEnabled && (
          <div className="credit-card">
            <IonItem lines="none">
              <IonLabel>{t("pay_order.eReceiptMsg")}</IonLabel>
              <IonCheckbox checked={eReceipt} onIonChange={(e) => setEReceipt(e.detail.checked)} />
            </IonItem>
            <IonItem lines="none">
              <IonLabel position="stacked">{t("profile.email")}</IonLabel>
              <IonInput disabled={!eReceipt} value={eReceiptMail} onIonChange={(e) => setEReceiptMail(e.detail.value!)} />
            </IonItem>
            {eCustomerEnabled && (
              <>
                <IonRow>
                  <IonCol size="12" className="link register-link">
                    <a href={tenant.settings?.fidelityAccount?.termsAndConditionsUrl || tenant.settings?.termsAndConditionsUrl} target="_blank">
                      <span>{t("table_payment.terms_pre")}</span>
                      <span> </span>
                      <span className="terms">{t("auth.terms")}</span>
                    </a>
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol size="12" className="link register-link">
                    <a href={tenant.settings?.fidelityAccount?.privacyPolicyUrl || tenant.settings?.privacyPolicyUrl || "https://www.cassaincloud.it/privacy"} target="_blank">
                      <span>{t("table_payment.privacy_pre")}</span>
                      <span> </span>
                      <span className="privacy">{t("auth.privacy")}</span>
                    </a>
                  </IonCol>
                </IonRow>
              </>
            )}
          </div>
        )}
        {!json || (json.state === "error" || json.state === "failed") && (
          <IonCard className="payment-error-card">
            <IonGrid>
              <IonRow>
                <IonCol size="2"><IonIcon className="icon" icon={warning} color="warning" /></IonCol>
                <IonCol size="10">
                  <IonCardHeader>
                    <IonCardTitle className="title">{t("pay_order.errorTitle")}</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>{getErrorString(json.declineCode)}</IonCardContent>
                </IonCol>
              </IonRow>
            </IonGrid>
          </IonCard>
        )}
      </IonContent>
      {json && json.state === "active" && eReceiptEnabled && (
        <IonFooter className="confirm-bar">
          <div className="content">
            <IonButton onClick={() => confirm()} expand="block">
              {t("new_order.confirm")}
            </IonButton>
          </div>
        </IonFooter>
      )}
      {!json || (json.state === "error" || json.state === "failed") && (
        <>
          <IonFooter className="confirm-bar">
            <div className="content">
              {sourceTypes && sourceTypes.length > 0 && (
                <IonButton onClick={() => pay()} expand="block">
                  {t("pay_order.retryPayment")}
                </IonButton>
              )}
              <IonButton onClick={() => history.replace(guestModal ? `/select/table/${selectedSalesPointId}/${idTable!}` : "/app/orders")} expand="block">
                {t("pay_order.cancel")}
              </IonButton>
            </div>
          </IonFooter>
          <IonActionSheet
            isOpen={showActionSheet}
            onDidDismiss={() => setShowActionSheet(false)}
            buttons={[
              {
                text: t("payment_types.CREDIT_CARD"),
                icon: card,
                handler: () => {
                  history.replace(guestModal ? `/tspay/${idTable!}/${idOrder}/${SourceType.CREDIT_CARD}` : `/app/orders/tspay/${idOrder}/${SourceType.CREDIT_CARD}`);
                },
              },
              {
                text: t("payment_types.PAYPAL"),
                icon: logoPaypal,
                handler: () => {
                  history.replace(guestModal ? `/tspay/${idTable!}/${idOrder}/${SourceType.PAYPAL}` : `/app/orders/tspay/${idOrder}/${SourceType.PAYPAL}`);
                },
              }
            ]}
          ></IonActionSheet>
        </>
      )}
    </IonPage>
  );
};

export default TsPayResponse;
